import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Autocomplete from "@mui/material/Autocomplete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import EngineeringIcon from "@mui/icons-material/Engineering";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SendIcon from "@mui/icons-material/Send";
import ForumIcon from "@mui/icons-material/Forum";
import PaidIcon from "@mui/icons-material/CheckCircle";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ViewListIcon from "@mui/icons-material/ViewList";
import GridViewIcon from "@mui/icons-material/GridView";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import ServiceOrderDialog from "../../components/ServiceOrderDialog";
import PixChargeDialog from "../../components/PixChargeDialog";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import ServiceOrderPrint, { printShippingLabel } from "../../components/ServiceOrderPrint";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SendToChatDialog from "../../components/SendToChatDialog";

const STATUS_LABELS = {
  aberta:       { label: "Aberta",        color: "#1976d2" },
  em_producao:  { label: "Em produção",   color: "#f37021" },
  pronta:       { label: "Pronta",        color: "#388e3c" },
  entregue:     { label: "Entregue",      color: "#757575" },
  cancelada:    { label: "Cancelada",     color: "#d32f2f" },
};

// Situação espelhada do Bling (definida pelo RH). Só VISUAL — não influencia o Bling.
// A Uiara "Atende" (9) os pedidos ao fechar o caixa (baixa estoque + gera conta) =
// "Verificado/Lançado no sistema". Selo verde destaca as OS já processadas.
const BLING_SITUACAO = {
  6: { label: "Em aberto", color: "#607d8b", launched: false },
  9: { label: "Verificado", color: "#2e7d32", launched: true },
  12: { label: "Cancelado", color: "#c62828", launched: false },
  15: { label: "Em andamento", color: "#1565c0", launched: false },
  18: { label: "Venda agenciada", color: "#6a1b9a", launched: true },
  21: { label: "Em digitação", color: "#8d6e63", launched: false },
  24: { label: "Verificado", color: "#00838f", launched: true },
  780710: { label: "Aguardando etiqueta", color: "#ef6c00", launched: false },
  780711: { label: "Em devolução", color: "#ad1457", launched: false },
};
const blingSituacaoOf = (id) => id == null ? null : (BLING_SITUACAO[id] || { label: `Situação ${id}`, color: "#607d8b", launched: false });

// Sequência linear de progresso (excluindo cancelada)
const STATUS_SEQUENCE = ["aberta", "em_producao", "pronta", "entregue"];

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  toolbar: { display: "flex", gap: 8, alignItems: "center", marginBottom: 16, flexWrap: "wrap" },
  title: { display: "flex", alignItems: "center", gap: 8, marginBottom: 16 },
  table: { minWidth: 700 },
  chip: { fontWeight: 600, fontSize: 11 },
  urgente: { color: "#d32f2f", fontWeight: 700 },
}));

const ServiceOrders = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todas");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [printOrder, setPrintOrder] = useState(null);

  const handlePrintLabel = async (o) => {
    const r = await printShippingLabel(o);
    if (r.reason === "sem_endereco") toast.warn(`OS ${o.numero} sem endereço de entrega`);
    else if (r.reason === "popup") toast.warn("O navegador bloqueou a janela da etiqueta — libere pop-ups para este site");
  };
  const [deleting, setDeleting] = useState(null);
  const [sendDialog, setSendDialog] = useState(null);
  const [pixOrder, setPixOrder] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(null);
  const [statusMenu, setStatusMenu] = useState(null); // { anchorEl, order }
  const [changingStatus, setChangingStatus] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState(null); // order | null
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [pmLoading, setPmLoading] = useState(false);
  const [pmError, setPmError] = useState(null);
  const [payForm, setPayForm] = useState({ methodId: "", methodName: "", discount: "", installments: 1 });
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("kr_os_view") || "list");
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("kr_os_sort") || "recentes");
  const [blingFilter, setBlingFilter] = useState("todos"); // todos | verificado | pendente
  const changeView = (mode) => { if (mode) { setViewMode(mode); localStorage.setItem("kr_os_view", mode); } };
  const changeSort = (v) => { setSortBy(v); localStorage.setItem("kr_os_sort", v); };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/service-orders", {
        params: { status: statusFilter, search, limit: 200 },
      });
      setOrders(data.rows || []);
      setCount(data.count || 0);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(fetchOrders, search ? 400 : 0);
    return () => clearTimeout(t);
  }, [fetchOrders, search]);

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir esta OS?")) return;
    setDeleting(id);
    try {
      await api.delete(`/service-orders/${id}`);
      toast.success("OS excluída");
      fetchOrders();
    } catch (err) {
      toastError(err);
    } finally {
      setDeleting(null);
    }
  };

  const handleSaved = () => {
    setDialogOpen(false);
    setEditingOrder(null);
    fetchOrders();
  };

  const handleStatusChange = async (order, newStatus) => {
    setStatusMenu(null);
    setChangingStatus(order.id);
    try {
      await api.put(`/service-orders/${order.id}`, { status: newStatus });
      toast.success(`OS ${order.numero} → ${STATUS_LABELS[newStatus]?.label || newStatus}`);
      fetchOrders();
    } catch (err) {
      toastError(err);
    } finally {
      setChangingStatus(null);
    }
  };

  const nextStatus = (current) => {
    const idx = STATUS_SEQUENCE.indexOf(current);
    return idx >= 0 && idx < STATUS_SEQUENCE.length - 1 ? STATUS_SEQUENCE[idx + 1] : null;
  };

  const openPaymentDialog = async (order) => {
    setPaymentDialog(order);
    setPayForm({ methodId: "", methodName: "", discount: "", installments: 1 });
    setPmError(null);
    setPmLoading(true);
    try {
      const { data } = await api.get("/bling/payment-methods");
      setPaymentMethods(Array.isArray(data) ? data : []);
    } catch (err) {
      setPmError("Não foi possível carregar as formas de pagamento do Bling.");
      setPaymentMethods([]);
    } finally {
      setPmLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    const order = paymentDialog;
    if (!order) return;
    setMarkingPaid(order.id);
    setPaymentDialog(null);
    try {
      await api.patch(`/service-orders/${order.id}/mark-paid`, {
        paid: true,
        paymentMethodId: payForm.methodId ? Number(payForm.methodId) : undefined,
        paymentMethodName: payForm.methodName || undefined,
        discount: payForm.discount ? parseFloat(String(payForm.discount)) : 0,
        installmentsCount: payForm.installments > 1 ? Number(payForm.installments) : 1,
      });
      toast.success(`OS ${order.numero} marcada como PAGA`);
      fetchOrders();
    } catch (err) {
      toastError(err);
    } finally {
      setMarkingPaid(null);
    }
  };

  const handleUnmarkPaid = async (order) => {
    if (!window.confirm(`Desfazer pagamento da OS ${order.numero}?`)) return;
    setMarkingPaid(order.id);
    try {
      await api.patch(`/service-orders/${order.id}/mark-paid`, { paid: false });
      toast.success("Pagamento desfeito");
      fetchOrders();
    } catch (err) {
      toastError(err);
    } finally {
      setMarkingPaid(null);
    }
  };

  const handleMarkPaid = (order, paid) => {
    if (paid) openPaymentDialog(order);
    else handleUnmarkPaid(order);
  };

  const fmtDate = (d) => d ? new Date(d + "T12:00:00").toLocaleDateString("pt-BR") : "—";
  const fmtMoney = (v) => v != null ? Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—";

  const isOverdue = (order) => {
    if (!order.deadline || ["entregue","cancelada"].includes(order.status)) return false;
    return new Date(order.deadline + "T23:59:59") < new Date();
  };

  const renderStatus = (o) => {
    const st = STATUS_LABELS[o.status] || { label: o.status, color: "#999" };
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {changingStatus === o.id ? (
          <CircularProgress size={16} style={{ color: st.color }} />
        ) : (
          <Chip
            label={st.label}
            size="small"
            className={classes.chip}
            onClick={(e) => setStatusMenu({ anchorEl: e.currentTarget, order: o })}
            style={{ backgroundColor: st.color + "22", color: st.color, borderColor: st.color + "55", border: "1px solid", cursor: "pointer" }}
          />
        )}
        {nextStatus(o.status) && changingStatus !== o.id && (
          <Tooltip title={`Avançar → ${STATUS_LABELS[nextStatus(o.status)]?.label}`}>
            <IconButton size="small" onClick={() => handleStatusChange(o, nextStatus(o.status))} style={{ padding: 2, color: STATUS_LABELS[nextStatus(o.status)]?.color }}>
              <ArrowForwardIcon style={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
        {renderBlingSelo(o)}
      </div>
    );
  };

  // Selo VISUAL da situação Bling (definida pelo RH; não influencia o Bling).
  // Só aparece se a situação já foi espelhada e não é "Em aberto" (6) — destaca as
  // OS que a Uiara já verificou/lançou no caixa.
  const renderBlingSelo = (o) => {
    const s = blingSituacaoOf(o.blingSituacaoId);
    if (!s || o.blingSituacaoId === 6) return null;
    return (
      <Tooltip title={`Situação no Bling: ${s.label} (definida pelo RH)`}>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 2, whiteSpace: "nowrap",
          background: s.color + "1f", color: s.color, border: `1px solid ${s.color}66`,
          borderRadius: 10, padding: "1px 7px", fontSize: "0.68rem", fontWeight: 700,
        }}>
          {s.launched ? "✓ " : ""}{s.label}
        </span>
      </Tooltip>
    );
  };

  const renderActions = (o) => (
    <>
      {!o.paidAt ? (
        <Tooltip title="Registrar pagamento">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={markingPaid === o.id ? <CircularProgress size={12} /> : <AttachMoneyIcon style={{ fontSize: 14 }} />}
              onClick={() => handleMarkPaid(o, true)}
              disabled={markingPaid === o.id}
              style={{ color: "#388e3c", borderColor: "#a5d6a7", fontSize: 11, padding: "2px 8px", minWidth: 0, textTransform: "none" }}
            >
              Pago
            </Button>
          </span>
        </Tooltip>
      ) : (
        <Tooltip title="Desfazer Pagamento">
          <IconButton size="small" onClick={() => handleMarkPaid(o, false)} disabled={markingPaid === o.id} style={{ color: "#9e9e9e" }}>
            {markingPaid === o.id ? <CircularProgress size={14} /> : <MoneyOffIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Imprimir / Ver PDF">
        <IconButton size="small" onClick={() => setPrintOrder(o)}>
          <PrintIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {(o.freteAtivo || o.freteEndereco) && (
        <Tooltip title="Etiqueta de envio">
          <IconButton size="small" onClick={() => handlePrintLabel(o)} style={{ color: "#F37021" }}>
            <LocalShippingIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {o.ticketId && (
        <Tooltip title="Ir ao atendimento vinculado">
          <IconButton size="small" onClick={() => navigate(`/tickets/${o.ticketId}`)} style={{ color: "#00897b" }}>
            <ForumIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Enviar no chat">
        <IconButton size="small" onClick={() => setSendDialog(o)} style={{ color: "#1565c0" }}>
          <SendIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {o.status !== "cancelada" && (
        <Tooltip title="Cobrar via Pix">
          <IconButton size="small" onClick={() => setPixOrder(o)} style={{ color: "#00897b" }}>
            <QrCode2Icon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Editar">
        <IconButton size="small" onClick={() => { setEditingOrder(o); setDialogOpen(true); }}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Excluir">
        <IconButton size="small" onClick={() => handleDelete(o.id)} disabled={deleting === o.id}>
          {deleting === o.id ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" style={{ color: "#d32f2f" }} />}
        </IconButton>
      </Tooltip>
    </>
  );

  const renderNumero = (o) => (
    <>
      <span style={{ fontWeight: 700, fontFamily: "monospace" }}>{o.numero}</span>
      {o.prioridade === "urgente" && (
        <Tooltip title="Urgente">
          <WarningAmberIcon style={{ fontSize: 14, color: "#d32f2f", marginLeft: 4, verticalAlign: "middle" }} />
        </Tooltip>
      )}
      {o.paidAt && (
        <Tooltip title={`Pago em ${new Date(o.paidAt).toLocaleDateString("pt-BR")}`}>
          <Chip label="PAGO" size="small" icon={<PaidIcon style={{ fontSize: 12 }} />}
            style={{ marginLeft: 6, backgroundColor: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7", fontSize: 10, height: 18, fontWeight: 700 }} />
        </Tooltip>
      )}
    </>
  );

  const blingLabel = (o) => o.blingOrderNumero ? `#${o.blingOrderNumero}` : (o.blingOrderId ? `Bling #${o.blingOrderId}` : "—");

  const sortedOrders = React.useMemo(() => {
    let arr = [...orders];
    if (blingFilter === "verificado") arr = arr.filter(o => (blingSituacaoOf(o.blingSituacaoId) || {}).launched);
    else if (blingFilter === "pendente") arr = arr.filter(o => !(blingSituacaoOf(o.blingSituacaoId) || {}).launched && o.status !== "cancelada");
    const num = (o) => parseInt(String(o.numero).replace(/\D/g, ""), 10) || 0;
    switch (sortBy) {
      case "prazo": // vencimento mais próximo primeiro (sem prazo por último)
        return arr.sort((a, b) => (a.deadline || "9999").localeCompare(b.deadline || "9999"));
      case "maior_valor":
        return arr.sort((a, b) => (Number(b.total) || 0) - (Number(a.total) || 0));
      case "cliente":
        return arr.sort((a, b) => (a.clientName || a.contact?.name || "").localeCompare(b.clientName || b.contact?.name || ""));
      case "numero":
        return arr.sort((a, b) => num(a) - num(b));
      case "recentes":
      default:
        return arr.sort((a, b) => num(b) - num(a));
    }
  }, [orders, sortBy, blingFilter]);

  return (
    <div className={classes.root}>
      <div className={classes.title}>
        <EngineeringIcon style={{ color: "#F37021", fontSize: 28 }} />
        <Typography variant="h5" style={{ fontWeight: 700 }}>Ordens de Serviço</Typography>
        <Typography variant="body2" color="textSecondary">({count} {count === 1 ? "OS" : "OS"})</Typography>
      </div>

      <div className={classes.toolbar}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Buscar número, cliente, pedido..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ minWidth: 260 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")} edge="end">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Select
          size="small"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          variant="outlined"
          style={{ minWidth: 160 }}
        >
          <MenuItem value="todas">Todos os status</MenuItem>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <MenuItem key={k} value={k}>{v.label}</MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={sortBy}
          onChange={e => changeSort(e.target.value)}
          variant="outlined"
          style={{ minWidth: 160 }}
        >
          <MenuItem value="recentes">Mais recentes</MenuItem>
          <MenuItem value="numero">Número (crescente)</MenuItem>
          <MenuItem value="prazo">Prazo (vence antes)</MenuItem>
          <MenuItem value="maior_valor">Maior valor</MenuItem>
          <MenuItem value="cliente">Cliente (A–Z)</MenuItem>
        </Select>
        <Select
          size="small"
          value={blingFilter}
          onChange={e => setBlingFilter(e.target.value)}
          variant="outlined"
          style={{ minWidth: 170 }}
        >
          <MenuItem value="todos">Bling: todos</MenuItem>
          <MenuItem value="verificado">✓ Verificados (Bling)</MenuItem>
          <MenuItem value="pendente">Pendentes de verificação</MenuItem>
        </Select>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewMode}
          onChange={(e, v) => changeView(v)}
        >
          <ToggleButton value="list" title="Lista"><ViewListIcon fontSize="small" /></ToggleButton>
          <ToggleButton value="grid" title="Grade"><GridViewIcon fontSize="small" /></ToggleButton>
        </ToggleButtonGroup>
        <div style={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditingOrder(null); setDialogOpen(true); }}
          style={{ backgroundColor: "#F37021", color: "#fff" }}
        >
          Nova OS
        </Button>
      </div>

      <Paper>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
            <CircularProgress style={{ color: "#F37021" }} />
          </div>
        ) : viewMode === "grid" ? (
          <div style={{ padding: 12 }}>
            {sortedOrders.length === 0 ? (
              <div style={{ padding: 32, textAlign: "center", color: "#999" }}>Nenhuma OS encontrada.</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
                {sortedOrders.map((o) => {
                  const overdue = isOverdue(o);
                  return (
                    <Paper key={o.id} variant="outlined" style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8, borderLeft: `4px solid ${(STATUS_LABELS[o.status] || {}).color || "#999"}` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                        <span>{renderNumero(o)}</span>
                        {renderStatus(o)}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#333" }}>
                        {o.clientName || o.contact?.name || "—"}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 12px", fontSize: 12, color: "#666" }}>
                        <span style={{ color: overdue ? "#d32f2f" : undefined, fontWeight: overdue ? 700 : 400 }}>
                          📅 {fmtDate(o.deadline)}{overdue && " (atrasado)"}
                        </span>
                        <span>💰 {fmtMoney(o.total)}</span>
                        <span>👤 {o.responsible?.name || "—"}</span>
                        <span>🧾 {blingLabel(o)}</span>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, borderTop: "1px solid #eee", paddingTop: 6 }}>
                        {renderActions(o)}
                      </div>
                    </Paper>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 700 }}>Número</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Cliente</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Prazo</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Total</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Responsável</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Pedido Bling</TableCell>
                <TableCell align="right" style={{ fontWeight: 700 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedOrders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" style={{ padding: 32, color: "#999" }}>
                    Nenhuma OS encontrada.
                  </TableCell>
                </TableRow>
              )}
              {sortedOrders.map((o) => {
                const overdue = isOverdue(o);
                return (
                  <TableRow key={o.id} hover>
                    <TableCell>
                      <span style={{ fontWeight: 700, fontFamily: "monospace" }}>{o.numero}</span>
                      {o.prioridade === "urgente" && (
                        <Tooltip title="Urgente">
                          <WarningAmberIcon style={{ fontSize: 14, color: "#d32f2f", marginLeft: 4, verticalAlign: "middle" }} />
                        </Tooltip>
                      )}
                      {o.paidAt && (
                        <Tooltip title={`Pago em ${new Date(o.paidAt).toLocaleDateString("pt-BR")}`}>
                          <Chip
                            label="PAGO"
                            size="small"
                            icon={<PaidIcon style={{ fontSize: 12 }} />}
                            style={{ marginLeft: 6, backgroundColor: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7", fontSize: 10, height: 18, fontWeight: 700 }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{o.clientName || o.contact?.name || "—"}</TableCell>
                    <TableCell>{renderStatus(o)}</TableCell>
                    <TableCell style={{ color: overdue ? "#d32f2f" : undefined, fontWeight: overdue ? 700 : 400 }}>
                      {fmtDate(o.deadline)}
                      {overdue && <span style={{ fontSize: 10, marginLeft: 4 }}>atrasado</span>}
                    </TableCell>
                    <TableCell>{fmtMoney(o.total)}</TableCell>
                    <TableCell>{o.responsible?.name || "—"}</TableCell>
                    <TableCell>{o.blingOrderNumero ? `#${o.blingOrderNumero}` : (o.blingOrderId ? `Bling #${o.blingOrderId}` : "—")}</TableCell>
                    <TableCell align="right">
                      {!o.paidAt ? (
                        <Tooltip title="Registrar pagamento">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={markingPaid === o.id ? <CircularProgress size={12} /> : <AttachMoneyIcon style={{ fontSize: 14 }} />}
                              onClick={() => handleMarkPaid(o, true)}
                              disabled={markingPaid === o.id}
                              style={{ color: "#388e3c", borderColor: "#a5d6a7", fontSize: 11, padding: "2px 8px", minWidth: 0, textTransform: "none" }}
                            >
                              Pago
                            </Button>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Desfazer Pagamento">
                          <IconButton size="small" onClick={() => handleMarkPaid(o, false)} disabled={markingPaid === o.id} style={{ color: "#9e9e9e" }}>
                            {markingPaid === o.id ? <CircularProgress size={14} /> : <MoneyOffIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Imprimir / Ver PDF">
                        <IconButton size="small" onClick={() => setPrintOrder(o)}>
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {(o.freteAtivo || o.freteEndereco) && (
                        <Tooltip title="Etiqueta de envio">
                          <IconButton size="small" onClick={() => handlePrintLabel(o)} style={{ color: "#F37021" }}>
                            <LocalShippingIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {o.ticketId && (
                        <Tooltip title="Ir ao atendimento vinculado">
                          <IconButton size="small" onClick={() => navigate(`/tickets/${o.ticketId}`)} style={{ color: "#00897b" }}>
                            <ForumIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Enviar no chat">
                        <IconButton size="small" onClick={() => setSendDialog(o)} style={{ color:"#1565c0" }}>
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {o.status !== "cancelada" && (
                        <Tooltip title="Cobrar via Pix">
                          <IconButton size="small" onClick={() => setPixOrder(o)} style={{ color: "#00897b" }}>
                            <QrCode2Icon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => { setEditingOrder(o); setDialogOpen(true); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => handleDelete(o.id)} disabled={deleting === o.id}>
                          {deleting === o.id ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" style={{ color: "#d32f2f" }} />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      {dialogOpen && (
        <ServiceOrderDialog
          open={dialogOpen}
          onClose={() => { setDialogOpen(false); setEditingOrder(null); }}
          onSaved={handleSaved}
          order={editingOrder}
        />
      )}

      {printOrder && (
        <ServiceOrderPrint
          order={printOrder}
          onClose={() => setPrintOrder(null)}
        />
      )}

      {sendDialog && (
        <SendToChatDialog
          open
          onClose={() => setSendDialog(null)}
          title={`Enviar OS ${sendDialog.numero} no chat`}
          subtitle={sendDialog.clientName ? `Cliente: ${sendDialog.clientName}` : undefined}
          ticketId={sendDialog.ticketId || null}
          sendEndpoint={(tid) => `/service-orders/ticket/${tid}/send/${sendDialog.id}`}
        />
      )}

      <PixChargeDialog
        open={Boolean(pixOrder)}
        order={pixOrder}
        onClose={() => { setPixOrder(null); fetchOrders(); }}
      />

      {/* Diálogo de pagamento */}
      <Dialog open={Boolean(paymentDialog)} onClose={() => setPaymentDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 8 }}>
          <AttachMoneyIcon style={{ color: "#388e3c" }} />
          Registrar Pagamento
          {paymentDialog && (
            <Typography variant="body2" color="textSecondary" style={{ marginLeft: 4 }}>
              — OS {paymentDialog.numero}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent style={{ paddingTop: 8 }}>
          {paymentDialog && (
            <Typography variant="body2" style={{ marginBottom: 16, color: "#555" }}>
              Total da OS: <strong>
                {Number(paymentDialog.total || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
              </strong>
            </Typography>
          )}

          {pmLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
              <CircularProgress size={24} style={{ color: "#388e3c" }} />
            </div>
          ) : pmError ? (
            <div style={{ padding: "8px 0" }}>
              <Typography variant="body2" color="error" style={{ marginBottom: 12 }}>{pmError}</Typography>
              <TextField
                label="Forma de pagamento (texto livre)"
                size="small"
                fullWidth
                variant="outlined"
                value={payForm.methodName}
                onChange={e => setPayForm(f => ({ ...f, methodName: e.target.value, methodId: "" }))}
              />
            </div>
          ) : (
            <Autocomplete
              options={paymentMethods}
              getOptionLabel={opt => opt.descricao || opt.nome || ""}
              value={paymentMethods.find(m => String(m.id) === String(payForm.methodId)) || null}
              onChange={(_, val) => setPayForm(f => ({
                ...f,
                methodId: val ? String(val.id) : "",
                methodName: val ? (val.descricao || val.nome || "") : "",
              }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Forma de pagamento"
                  variant="outlined"
                  size="small"
                  fullWidth
                  style={{ marginBottom: 16 }}
                />
              )}
              noOptionsText="Nenhuma forma encontrada"
              style={{ marginBottom: 16 }}
            />
          )}

          <TextField
            label="Desconto (R$)"
            size="small"
            fullWidth
            variant="outlined"
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            value={payForm.discount}
            onChange={e => setPayForm(f => ({ ...f, discount: e.target.value }))}
            style={{ marginBottom: 16 }}
            helperText={payForm.discount > 0 && paymentDialog
              ? `Total com desconto: ${(Number(paymentDialog.total || 0) - parseFloat(payForm.discount || 0)).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}`
              : undefined
            }
          />

          <TextField
            label="Número de parcelas"
            size="small"
            fullWidth
            variant="outlined"
            select
            value={payForm.installments}
            onChange={e => setPayForm(f => ({ ...f, installments: Number(e.target.value) }))}
          >
            {[1,2,3,4,5,6,8,10,12].map(n => (
              <MenuItem key={n} value={n}>{n === 1 ? "À vista" : `${n}x`}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions style={{ padding: "8px 24px 16px" }}>
          <Button onClick={() => setPaymentDialog(null)} style={{ textTransform: "none" }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmPayment}
            style={{ backgroundColor: "#388e3c", color: "#fff", textTransform: "none" }}
            startIcon={<PaidIcon />}
          >
            Confirmar Pagamento
          </Button>
        </DialogActions>
      </Dialog>

      {/* Menu de mudança rápida de status */}
      <Menu
        anchorEl={statusMenu?.anchorEl}
        open={Boolean(statusMenu)}
        onClose={() => setStatusMenu(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {Object.entries(STATUS_LABELS).map(([key, val]) => (
          <MenuItem
            key={key}
            disabled={key === statusMenu?.order?.status}
            onClick={() => handleStatusChange(statusMenu.order, key)}
            style={{ fontSize: 13 }}
          >
            <span style={{
              display: "inline-block", width: 10, height: 10, borderRadius: "50%",
              backgroundColor: val.color, marginRight: 8,
            }} />
            {val.label}
            {key === statusMenu?.order?.status && (
              <span style={{ marginLeft: 6, fontSize: 11, color: "#999" }}>atual</span>
            )}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default ServiceOrders;
